import React, { useState, useEffect, useCallback, useRef } from 'react';
import kpiCategoryService, { KPICategory } from '../../services/kpiCategoryService';
import categoryKPIService, { CategoryKPI } from '../../services/categoryKPIService';
import { useTheme } from '../../contexts/ThemeContext';
import './KPICategorySelector.css';

interface KPISelection {
  categoryId: number;
  categoryName: string;
  selectedKPIs: CategoryKPI[];
}

interface KPICategorySelectorProps {
  onSelectionChange: (selections: KPISelection[]) => void;
  initialSelections?: KPISelection[];
}

const KPICategorySelector: React.FC<KPICategorySelectorProps> = ({
  onSelectionChange,
  initialSelections = []
}) => {
  const { theme } = useTheme();
  const [categories, setCategories] = useState<KPICategory[]>([]);
  const [categoryKPIs, setCategoryKPIs] = useState<Record<number, CategoryKPI[]>>({});
  const [selectedKPIs, setSelectedKPIs] = useState<Record<number, Set<number>>>({}); // categoryId -> Set of KPI IDs
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<KPICategory | null>(null);
  const [overlayLoading, setOverlayLoading] = useState(false);

  // Initialize selections from props
  useEffect(() => {
    if (initialSelections.length > 0) {
      const newSelectedKPIs: Record<number, Set<number>> = {};

      initialSelections.forEach(selection => {
        newSelectedKPIs[selection.categoryId] = new Set(
          selection.selectedKPIs.map(kpi => kpi.id!).filter(id => id !== undefined)
        );
      });

      setSelectedKPIs(newSelectedKPIs);
    }
  }, [initialSelections]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Force light mode background colors on KPI cards and ALL child elements
  useEffect(() => {
    if (theme === 'light') {
      const forceKPICardBackgrounds = () => {
        // Target the KPI cards themselves
        const kpiCards = document.querySelectorAll('.kpi-card');
        kpiCards.forEach((card) => {
          const element = card as HTMLElement;
          element.style.setProperty('background', 'white', 'important');
          element.style.setProperty('background-color', 'white', 'important');
          element.style.setProperty('background-image', 'none', 'important');
          
          // Also target ALL child elements within each card
          const childElements = element.querySelectorAll('*');
          childElements.forEach((child) => {
            const childElement = child as HTMLElement;
            childElement.style.setProperty('background', 'transparent', 'important');
            childElement.style.setProperty('background-color', 'transparent', 'important');
            childElement.style.setProperty('background-image', 'none', 'important');
          });
        });

        // Target specific child elements by class
        const elementsToFix = [
          '.kpi-checkbox',
          '.kpi-details', 
          '.kpi-header',
          '.kpi-units',
          '.kpi-name',
          '.kpi-description'
        ];
        
        elementsToFix.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element) => {
            const el = element as HTMLElement;
            el.style.setProperty('background', 'transparent', 'important');
            el.style.setProperty('background-color', 'transparent', 'important');
            el.style.setProperty('background-image', 'none', 'important');
          });
        });
      };

      // Force immediately
      forceKPICardBackgrounds();
      
      // Also force after delays to catch dynamically loaded elements
      const timer1 = setTimeout(forceKPICardBackgrounds, 100);
      const timer2 = setTimeout(forceKPICardBackgrounds, 500);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [theme, categoryKPIs, selectedCategory]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedCategories = await kpiCategoryService.getKPICategories();
      setCategories(fetchedCategories);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load KPI categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = async (category: KPICategory) => {
    setSelectedCategory(category);
    
    // If KPIs for this category aren't loaded yet, load them
    if (!categoryKPIs[category.id!]) {
      setOverlayLoading(true);
      try {
        const kpis = await categoryKPIService.getCategoryKPIs(category.id!);
        setCategoryKPIs(prev => ({
          ...prev,
          [category.id!]: kpis
        }));
      } catch (err) {
        console.error('Error fetching KPIs for category:', err);
      } finally {
        setOverlayLoading(false);
      }
    }
  };

  const handleCloseOverlay = () => {
    setSelectedCategory(null);
  };

  const toggleKPI = (categoryId: number, kpiId: number) => {
    setSelectedKPIs(prev => {
      const categorySelections = prev[categoryId] || new Set();
      const newSelections = new Set(categorySelections);
      
      if (newSelections.has(kpiId)) {
        newSelections.delete(kpiId);
      } else {
        newSelections.add(kpiId);
      }
      
      const updatedSelections = {
        ...prev,
        [categoryId]: newSelections
      };
      
      // Notify parent component of changes
      notifySelectionChange(updatedSelections);
      
      return updatedSelections;
    });
  };

  const notifySelectionChange = (selections: Record<number, Set<number>>) => {
    const selectionArray: KPISelection[] = Object.entries(selections)
      .filter(([_, kpiIds]) => kpiIds.size > 0)
      .map(([categoryIdStr, kpiIds]) => {
        const categoryId = parseInt(categoryIdStr);
        const category = categories.find(c => c.id === categoryId);
        const selectedKPIs = Array.from(kpiIds)
          .map(kpiId => categoryKPIs[categoryId]?.find(kpi => kpi.id === kpiId))
          .filter(kpi => kpi !== undefined) as CategoryKPI[];
        
        return {
          categoryId,
          categoryName: category?.category_name || '',
          selectedKPIs
        };
      });
    
    onSelectionChange(selectionArray);
  };

  const fetchCategoryKPIs = async (categoryId: number) => {
    try {
      if (categoryKPIs[categoryId]) {
        return; // Already loaded
      }

      const kpis = await categoryKPIService.getCategoryKPIs(categoryId);
      setCategoryKPIs(prev => ({
        ...prev,
        [categoryId]: kpis
      }));
    } catch (err) {
      console.error(`Error fetching KPIs for category ${categoryId}:`, err);
    }
  };







  const getSelectedCount = (categoryId: number): number => {
    return selectedKPIs[categoryId]?.size || 0;
  };

  const getTotalKPICount = (categoryId: number): number => {
    return categoryKPIs[categoryId]?.length || 0;
  };

  if (loading) {
    return (
      <div className="kpi-selector-loading">
        <div className="loading-spinner"></div>
        <p>Loading KPI categories...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kpi-selector-error">
        <p>{error}</p>
        <button onClick={fetchCategories} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="selector-header">
        <h3>Select KPIs by Category</h3>
        <p>Choose the KPIs you want to include in your rule</p>
      </div>

      <div className="categories-container">
        {categories.map(category => (
          <div 
            key={category.id} 
            className="category-rectangle"
            onClick={() => handleCategorySelect(category)}
          >
            <span className="category-name">{category.category_name}</span>
          </div>
        ))}
      </div>

      {/* KPI Selection Overlay */}
      {selectedCategory && (
        <div className="kpi-overlay">
          <div className="kpi-overlay-content">
            <div className="kpi-overlay-header">
              <h3>{selectedCategory.category_name} KPIs</h3>
              <button className="close-overlay-button" onClick={handleCloseOverlay}>
                ×
              </button>
            </div>
            
            <div className="kpi-overlay-body">
              {overlayLoading ? (
                <div className="overlay-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading KPIs...</p>
                </div>
              ) : categoryKPIs[selectedCategory.id!] ? (
                categoryKPIs[selectedCategory.id!].length > 0 ? (
                  <div className="kpi-grid">
                    {categoryKPIs[selectedCategory.id!].map(kpi => (
                      <div key={kpi.id} className="kpi-card">
                        <label className="kpi-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedKPIs[selectedCategory.id!]?.has(kpi.id!) || false}
                            onChange={() => toggleKPI(selectedCategory.id!, kpi.id!)}
                          />
                          <div className="kpi-details">
                            <div className="kpi-header">
                              <span className="kpi-name">{kpi.kpiName}</span>
                            </div>
                            {kpi.measurementType && (
                              <div className="kpi-units">
                                <span className="units-label">Units:</span>
                                <span className="units-value">{kpi.measurementType}</span>
                              </div>
                            )}
                            {kpi.description && kpi.description !== kpi.kpiName && (
                              <p className="kpi-description">{kpi.description}</p>
                            )}
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-category">
                    <p>No KPIs found in this category</p>
                  </div>
                )
              ) : (
                <div className="overlay-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading KPIs...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {categories.length === 0 && (
        <div className="empty-state">
          <p>No KPI categories available</p>
          <p className="empty-subtitle">Contact your administrator to set up KPI categories</p>
        </div>
      )}
    </>
  );
};

export default KPICategorySelector;
